import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import {
  ArrowLeft, BadgePercent, Building2, ChevronUp, CircleHelp, Gem, Loader2, Minus, Plus, ShieldCheck, Sprout,
  TicketPercent, Users, Wallet, X, Zap,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { ErrorState, Loading } from '@/components/shared/feedback'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  useBillingMethods, useBillingPlans, useBillingSummary, useCheckout, useInvoiceProfiles, useQuote, useRequestPayment,
  type PaymentMethod, type PriceTier, type PricingConfig, type PricingPlan, type Quote, type QuotaKey, type Quotas,
} from '@/hooks/use-billing'
import { useAuthStore } from '@/stores/auth-store'
import { apiError } from '@/lib/api-client'
import { formatVnd } from '@/lib/format-money'
import { i18n } from '@/lib/i18n'
import { cn, formatNumber } from '@/lib/utils'
import {
  downgradeNote, MAX_DAYS, QUOTA_META, buildPlans, discountForDays, durationBadge, estimateActivationEnd, initialQuantities,
  isBlockedDowngrade, isFreeOrder, isPromoBlocked, minDays, nextDiscountTier, normalizePromoInput, purchaseFloor, purchaseModeNote,
  requantityWarning, resolvePlan, stepperBounds, daysLeft, vatBreakdown, vatLabel, vatListNote,
} from './plans'
import { DEFAULT_METHOD, METHOD_COPY, formatUsd, goToPayment, resultPath, safeFrom, usdCentsOf, writePendingOrder } from './payment-methods'
import { MethodLogos } from './payment-logos'
import { buildInvoiceBody, invoiceSummary, type InvoiceChoice, type InvoiceErrors } from './invoice-info'
import { InvoiceSection, initialChoice } from './invoice-section'
import { PaymentReturn } from './billing-page'

const PLAN_ICONS = [Sprout, Zap, Gem, Building2]

/**
 * "Thay đổi gói" — theo mẫu SaleSmartly /next/fee/pay/upgrade:
 * trái: chọn gói → thời hạn (nhãn giảm giá ở góc) → chi tiết gói (nhân viên, kênh chính thức,
 * tài khoản cá nhân — bấm +/−) → phương thức thanh toán; phải: xem trước đơn hàng + Đặt hàng.
 * Số tiền lấy từ POST /billing/quote (cùng công thức server dùng khi thanh toán).
 */
export function UpgradePage() {
  const { t } = useTranslation('billing')
  const [params, setParams] = useSearchParams()
  const summary = useBillingSummary()
  const catalog = useBillingPlans()
  const methods = useBillingMethods()
  const [chosenMethod, setChosenMethod] = useState<PaymentMethod | null>(null)
  const method = pickMethod(methods.data?.methods ?? [], chosenMethod)
  const role = useAuthStore((s) => s.user?.role)
  const userEmail = useAuthStore((s) => s.user?.email) ?? ''
  const canPay = role === 'owner' || role === 'admin'
  const profiles = useInvoiceProfiles(canPay)
  const [invoice, setInvoice] = useState<InvoiceChoice | null>(null)
  const [invoiceErrors, setInvoiceErrors] = useState<InvoiceErrors>({})
  // Danh bạ tải xong (hoặc lỗi) → chọn sẵn mục mặc định.
  useEffect(() => {
    if (invoice || !canPay || profiles.isLoading) return
    setInvoice(initialChoice(profiles.data, userEmail))
  }, [invoice, canPay, profiles.isLoading, profiles.data, userEmail])

  const cfg = catalog.data
  const plans = useMemo(() => (cfg ? buildPlans(cfg).filter((p) => p.purchasable) : []), [cfg])
  const s = summary.data
  const current = cfg && s ? resolvePlan(s.wasDowngraded ? 'free' : s.plan, cfg.plans) : null
  // "Mua rồi không trả lại": gói trả phí còn hạn → không chọn được gói thấp hơn (server cũng chặn).
  const upgradeOnly = !!s?.upgradeOnly
  const isBlocked = (id: string) => !!cfg && !!current && isBlockedDowngrade(upgradeOnly, current, id, cfg.plans)
  const selectable = plans.filter((p) => !isBlocked(p.id))
  const planId = selectable.find((p) => p.id === params.get('plan'))?.id ?? selectable.find((p) => p.highlight)?.id ?? selectable[0]?.id
  const plan = selectable.find((p) => p.id === planId)
  const currentDef = cfg && current?.id ? cfg.plans.find((p) => p.id === current.id) : undefined
  // Số tối thiểu khi gói còn hạn: không thấp hơn số đang hiệu lực (cùng gói / nâng gói).
  const floor = useMemo(
    () => (plan ? purchaseFloor(plan, { upgradeOnly, currentPlan: currentDef, purchased: s?.purchased }) : null),
    [plan, upgradeOnly, currentDef, s?.purchased],
  )

  // Số ngày: nhận từ `?days=` (quay về từ cổng thanh toán giữ nguyên lựa chọn).
  const [days, setDays] = useState<number | null>(() => {
    const d = Number(params.get('days'))
    return Number.isInteger(d) && d > 0 ? d : null
  })
  const [qty, setQty] = useState<Quotas | null>(null)
  // Mã ưu đãi đang áp — nhận sẵn từ link `/billing/upgrade?promo=MA` (server kiểm lại khi báo giá + thanh toán).
  const [promoCode, setPromoCode] = useState(() => normalizePromoInput(params.get('promo') ?? ''))
  const applyPromo = (code: string) => {
    setPromoCode(code)
    setParams((prev) => {
      const n = new URLSearchParams(prev)
      if (code) n.set('promo', code)
      else n.delete('promo')
      return n
    }, { replace: true })
  }

  const samePlan = !!s && !s.wasDowngraded && s.effectivePlan === planId
  // Đổi gói → điền lại số lượng theo gói mới (giữ số đang dùng thực tế, không dưới số tối thiểu).
  useEffect(() => {
    if (!plan || !s) return
    setQty(initialQuantities(plan, {
      purchased: s.purchased, samePlan, floor,
      usage: { members: s.usage.members, officialChannels: s.usage.channelsOfficial, personalChannels: s.usage.channelsPersonal },
    }))
  }, [plan, s, samePlan, floor])

  const effDays = days ?? cfg?.defaultDays ?? 30
  const quote = useQuote(plan && qty ? { planId: plan.id, days: effDays, quantities: qty, ...(promoCode ? { promoCode } : {}) } : null)

  if (summary.isLoading || catalog.isLoading) return <Loading />
  if (summary.isError || !s || catalog.isError || !cfg || !current) return <ErrorState message={t('upgrade.loadPlansError')} />
  if (!plan) {
    return (
      <ErrorState message={plans.length > 0 && upgradeOnly
        ? t('upgrade.noHigherPlan')
        : t('upgrade.noPurchasablePlan')} />
    )
  }

  const sameActive = samePlan && !!s.expiresAt && new Date(s.expiresAt).getTime() > Date.now()
  const mode = quote.data?.mode
  const periodEnd = estimateActivationEnd({ quote: quote.data, days: effDays, sameActive, expiresAt: s.expiresAt })

  return (
    <div className="-m-4 flex min-h-full flex-col bg-card md:-m-6">
      <header className="flex h-14 shrink-0 items-center gap-3 border-b px-5">
        <Link to="/billing" className="flex items-center gap-1.5 text-sm text-foreground hover:text-primary">
          <ArrowLeft className="h-4 w-4" /> {t('common:actions.back')}
        </Link>
        <span className="h-4 w-px bg-border" />
        <h1 className="text-base font-medium">{sameActive ? (mode === 'requantity' ? t('upgrade.titleRequantity') : t('upgrade.titleRenew')) : t('upgrade.titleChangePlan')}</h1>
      </header>

      {/* Quay về từ cổng thanh toán → hiện kết quả ngay tại trang đang thao tác. Đơn đã trả → gỡ mã ưu đãi (B9). */}
      <div className="px-5 pt-4 empty:hidden lg:px-6">
        <PaymentReturn onPaid={() => setPromoCode('')} />
      </div>

      <div className="grid flex-1 grid-cols-1 lg:grid-cols-[minmax(0,1fr)_380px]">
        <div className="space-y-7 p-5 lg:p-6">
          <Section title={t('upgrade.chooseUpgradePlan')}>
            {upgradeOnly && (
              <p className="rounded-lg border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
                {t('upgrade.upgradeOnlyNote')}
              </p>
            )}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {plans.map((p) => {
                const Icon = PLAN_ICONS[cfg.plans.findIndex((x) => x.id === p.id) % PLAN_ICONS.length] ?? Gem
                const on = p.id === plan.id
                const blocked = isBlocked(p.id)
                return (
                  <OptionCard key={p.id} selected={on} disabled={blocked} onClick={() => setParams((prev) => { const n = new URLSearchParams(prev); n.set('plan', p.id); return n }, { replace: true })} className="p-4">
                    <div className="flex items-start gap-3">
                      <Radio on={on} className="mt-1" />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <span className="flex items-center gap-1.5 font-medium">
                            <Icon className={cn('h-4 w-4', on ? 'text-primary' : 'text-muted-foreground')} /> {p.name}
                            {current.id === p.id && <span className="rounded bg-muted px-1.5 text-xs font-normal text-muted-foreground">{t('plans.inUse')}</span>}
                          </span>
                          <span className="shrink-0 font-medium tabular-nums">{formatVnd(p.monthlyPrice ?? 0)}<span className="text-xs font-normal text-muted-foreground">{t('upgrade.perMonthSuffix')}</span></span>
                        </div>
                        <p className="mt-1 text-sm text-muted-foreground">{p.description}</p>
                        {blocked && <p className="mt-1.5 text-xs font-medium text-muted-foreground">{downgradeNote()}</p>}
                      </div>
                    </div>
                  </OptionCard>
                )
              })}
            </div>
          </Section>

          <Section title={t('upgrade.chooseDuration')}>
            <DurationPicker cfg={cfg} days={effDays} onChange={setDays} />
          </Section>

          <Section title={t('upgrade.planDetails')}>
            {sameActive && canPay && (
              <p className="flex flex-wrap items-center gap-1 rounded-lg border border-primary/20 bg-primary/[0.04] px-3 py-2 text-xs text-muted-foreground">
                {t('upgrade.addonHintText')}
                <Link to="/billing/addon" className="font-medium text-primary hover:underline">{t('upgrade.addonHintLink')}</Link>
              </p>
            )}
            <div className="grid grid-cols-1 gap-4 rounded-xl border p-4 md:grid-cols-[1fr_2fr]">
              <QuotaGroup title={t('addon.quotaLabels.members')} icon={Users}>
                <QuotaStepper k="members" plan={plan} tiers={cfg.addons.members} qty={qty} setQty={setQty} used={s.usage.members}
                  floor={floor?.members} floorLabel={floorLabelOf(floor, plan, 'members')} />
              </QuotaGroup>
              <QuotaGroup title={t('addon.socialAccountsTitle')} icon={Wallet}>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <QuotaStepper k="officialChannels" plan={plan} tiers={cfg.addons.officialChannels} qty={qty} setQty={setQty} used={s.usage.channelsOfficial}
                  floor={floor?.officialChannels} floorLabel={floorLabelOf(floor, plan, 'officialChannels')} />
                  <QuotaStepper k="personalChannels" plan={plan} tiers={cfg.addons.personalChannels} qty={qty} setQty={setQty} used={s.usage.channelsPersonal}
                  floor={floor?.personalChannels} floorLabel={floorLabelOf(floor, plan, 'personalChannels')} />
                </div>
              </QuotaGroup>
            </div>
          </Section>

          <Section title={t('addon.paymentMethodTitle')}>
            <PaymentMethods methods={methods.data?.methods ?? []} loading={methods.isLoading} selected={method} onSelect={setChosenMethod} />
          </Section>

          {canPay && invoice && (
            <Section title={t('addon.invoiceInfoTitle')} id="invoice-info">
              <InvoiceSection
                notReady={methods.data?.invoiceReady === false}
                choice={invoice}
                onChange={(c) => { setInvoice(c); if (Object.keys(invoiceErrors).length) setInvoiceErrors({}) }}
                profiles={profiles.data ?? []}
                loadingProfiles={profiles.isLoading}
                errors={invoiceErrors}
                defaultEmail={userEmail}
              />
            </Section>
          )}
        </div>

        <OrderPreview
          currentName={current.name}
          currentExpires={s.expiresAt}
          planName={plan.name}
          days={effDays}
          periodEnd={periodEnd}
          sameActive={sameActive}
          quote={quote}
          canPay={canPay}
          method={method}
          payload={qty ? { planId: plan.id, days: effDays, quantities: qty } : null}
          promoCode={promoCode}
          onPromoChange={applyPromo}
          invoice={invoice}
          invoiceLabel={invoiceSummary(invoice, profiles.data ?? [])}
          onInvoiceErrors={setInvoiceErrors}
        />
      </div>
    </div>
  )
}

// ── Khối trái ────────────────────────────────────────────────────────

/**
 * Ô nhập số ngày + 4 gợi ý (các mốc trong bảng giá, kèm nhãn giảm giá). Bấm gợi ý điền số ngày vào ô;
 * % giảm theo mốc lớn nhất ≤ số ngày (server tính lại khi báo giá / thanh toán).
 */
function DurationPicker({ cfg, days, onChange }: { cfg: PricingConfig; days: number; onChange: (d: number) => void }) {
  const { t } = useTranslation('billing')
  const [draft, setDraft] = useState(String(days))
  useEffect(() => setDraft(String(days)), [days])
  const lo = minDays(cfg)
  const n = Number(draft)
  const valid = draft !== '' && Number.isInteger(n) && n >= lo && n <= MAX_DAYS
  // Báo giá theo số ngày đang gõ (trễ nhẹ để không gọi API mỗi phím).
  useEffect(() => {
    if (!valid || n === days) return
    const t = setTimeout(() => onChange(n), 400)
    return () => clearTimeout(t)
  }, [draft]) // eslint-disable-line react-hooks/exhaustive-deps
  const commit = () => {
    if (!valid) setDraft(String(Math.min(MAX_DAYS, Math.max(lo, Number.isFinite(n) && n > 0 ? Math.round(n) : days))))
  }
  const pct = discountForDays(cfg, valid ? n : days) ?? 0
  const next = nextDiscountTier(cfg, valid ? n : days)
  const suggestions = [...cfg.durations].sort((a, b) => a.days - b.days).slice(0, 4)

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <div className={cn('flex h-10 w-44 items-stretch overflow-hidden rounded-lg border bg-card focus-within:border-primary focus-within:ring-1 focus-within:ring-primary', !valid && 'border-destructive')}>
          <input
            inputMode="numeric"
            aria-label={t('upgrade.daysToBuy')}
            value={draft}
            onChange={(e) => setDraft(e.target.value.replace(/\D/g, '').slice(0, 4))}
            onBlur={commit}
            onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur() }}
            className="min-w-0 flex-1 bg-transparent px-3 text-base font-medium tabular-nums outline-none"
          />
          <span className="flex items-center border-l bg-muted/50 px-3 text-sm text-muted-foreground">{t('upgrade.daysUnit')}</span>
        </div>
        {pct > 0 && (
          <span className="inline-flex items-center gap-1 rounded-full bg-orange-500/10 px-2.5 py-1 text-xs font-medium text-orange-600 dark:text-orange-400">
            <BadgePercent className="h-3.5 w-3.5" /> {t('upgrade.currentDiscount', { pct })}
          </span>
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        {suggestions.map((d) => {
          const on = valid && n === d.days
          const badge = durationBadge(d)
          return (
            <button
              key={d.days}
              type="button"
              onClick={() => { setDraft(String(d.days)); onChange(d.days) }}
              aria-pressed={on}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm transition-colors',
                on ? 'border-primary bg-primary/[0.06] font-medium text-primary' : 'hover:border-primary/40 hover:bg-muted/60',
              )}
            >
              {t('plans.termLabel', { days: d.days })}
              {badge && <span className="rounded-full bg-gradient-to-r from-orange-500 to-rose-500 px-1.5 text-[11px] font-medium text-white">{badge}</span>}
            </button>
          )
        })}
      </div>
      <p className={cn('text-xs', valid ? 'text-muted-foreground' : 'text-destructive')}>
        {valid
          ? next
            ? t('upgrade.daysHintWithNext', { lo, max: MAX_DAYS, nextDays: next.days, nextPercent: next.discountPercent })
            : t('upgrade.daysHint', { lo, max: MAX_DAYS })
          : t('upgrade.daysRangeError', { lo, max: MAX_DAYS })}
      </p>
    </div>
  )
}

export function Section({ title, children, id }: { title: string; children: ReactNode; id?: string }) {
  return (
    <section id={id} className="space-y-3">
      <h2 className="text-[15px] font-medium">{title}</h2>
      {children}
    </section>
  )
}

function Radio({ on, className }: { on: boolean; className?: string }) {
  return (
    <span
      className={cn(
        'flex h-4 w-4 shrink-0 items-center justify-center rounded-full border transition-colors',
        on ? 'border-primary bg-primary' : 'border-muted-foreground/40 bg-card',
        className,
      )}
    >
      {on && <span className="h-1.5 w-1.5 rounded-full bg-white" />}
    </span>
  )
}

/** Thẻ lựa chọn — cùng hiệu ứng với các màn khác: viền xanh + nền xanh nhạt khi chọn, nhấc nhẹ khi rê. */
function OptionCard({ selected, onClick, className, children, disabled }: {
  selected: boolean; onClick: () => void; className?: string; children: ReactNode; disabled?: boolean
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      aria-disabled={disabled || undefined}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'relative rounded-xl border bg-card text-left text-sm transition-all duration-150',
        disabled
          ? 'cursor-not-allowed opacity-60'
          : selected
            ? 'border-primary bg-primary/[0.04] shadow-[0_0_0_1px_hsl(var(--primary))]'
            : 'hover:-translate-y-px hover:border-primary/40 hover:shadow-md',
        className,
      )}
    >
      {children}
    </button>
  )
}

/** Nhãn số tối thiểu: "Đang có" khi tối thiểu là số đang hiệu lực (cao hơn số gồm sẵn), còn lại "Gồm sẵn". */
function floorLabelOf(floor: Quotas | null, plan: PricingPlan, k: QuotaKey): string {
  return floor && floor[k] > plan.included[k] ? i18n.t('billing:addon.floorLabel') : i18n.t('billing:plans.included')
}

export function QuotaGroup({ title, icon: Icon, children }: { title: string; icon: typeof Users; children: ReactNode }) {
  return (
    <div className="space-y-3">
      <p className="flex items-center gap-1.5 text-sm font-medium"><Icon className="h-4 w-4 text-primary" /> {title}</p>
      {children}
    </div>
  )
}

export function QuotaStepper({
  k, plan, tiers, qty, setQty, used, floor, floorLabel, highlight = false,
}: {
  k: QuotaKey
  plan: PricingPlan
  tiers: PriceTier[]
  qty: Quotas | null
  setQty: (q: Quotas) => void
  used: number
  /** Số tối thiểu (mặc định = số gồm sẵn của gói; "Mua thêm" = số đang có). */
  floor?: number
  /** Nhãn của số tối thiểu, vd "Đang có". */
  floorLabel?: string
  /** Đến từ popup "đã đạt hạn mức" (`?quota=`) — viền nổi bật + cuộn tới khi vào trang. */
  highlight?: boolean
}) {
  const { t } = useTranslation('billing')
  const meta = QUOTA_META[k]
  // B8: không cho chọn dưới số đang dùng thực tế (server cũng chặn — 400 QUANTITY_BELOW_USAGE).
  const bounds = stepperBounds(plan, k, { floor, used, floorLabel: floorLabel ?? t('plans.included') })
  const { min, max } = bounds
  const base = Math.max(plan.included[k], floor ?? 0)
  const value = qty?.[k] ?? min
  const [draft, setDraft] = useState<string | null>(null)
  const set = (n: number) => {
    if (!qty || !Number.isFinite(n)) return
    const v = Math.max(min, max != null ? Math.min(max, Math.round(n)) : Math.round(n))
    setQty({ ...qty, [k]: v })
  }
  const extra = value - base
  const fixed = max != null && max <= min

  return (
    <div
      id={highlight ? `quota-stepper-${k}` : undefined}
      className={cn('rounded-lg border bg-muted/30 p-3', highlight && 'ring-2 ring-primary')}
    >
      <div className="mb-2 flex items-center gap-1 text-sm">
        <span>{meta.label}</span>
        <TierHelp hint={meta.hint} tiers={tiers} included={plan.included[k]} unit={meta.unit} />
      </div>
      <div className="flex h-9 items-stretch overflow-hidden rounded-md border bg-card">
        <button type="button" aria-label={t('upgrade.decrease')} disabled={fixed || value <= min} onClick={() => set(value - 1)}
          className="flex w-9 items-center justify-center border-r text-muted-foreground hover:bg-muted disabled:opacity-40">
          <Minus className="h-4 w-4" />
        </button>
        <input
          inputMode="numeric"
          aria-label={meta.label}
          disabled={fixed}
          value={draft ?? String(value)}
          onChange={(e) => setDraft(e.target.value.replace(/\D/g, ''))}
          onBlur={() => { if (draft != null) set(Number(draft || min)); setDraft(null) }}
          onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur() }}
          className="min-w-0 flex-1 bg-transparent px-3 text-sm tabular-nums outline-none"
        />
        <button type="button" aria-label={t('upgrade.increase')} disabled={fixed || (max != null && value >= max)} onClick={() => set(value + 1)}
          className="flex w-9 items-center justify-center border-l text-muted-foreground hover:bg-muted disabled:opacity-40">
          <Plus className="h-4 w-4" />
        </button>
      </div>
      <p className="mt-1.5 text-xs text-muted-foreground">
        {bounds.label} {formatNumber(min)}{extra > 0 && <span className="text-primary"> · {t('upgrade.buyMore', { count: formatNumber(extra) })}</span>}
        {max != null && ` · ${t('upgrade.maxOf', { max: formatNumber(max) })}`}
        {bounds.overMax && <span className="text-destructive"> · {t('upgrade.overMaxNote', { used: formatNumber(used) })}</span>}
      </p>
    </div>
  )
}

/** Dấu hỏi: rê vào hiện bảng giá luỹ tiến (như popover của SaleSmartly). */
function TierHelp({ hint, tiers, included, unit }: { hint: string; tiers: PriceTier[]; included: number; unit: string }) {
  const { t } = useTranslation('billing')
  let from = 1
  return (
    <span className="group/help relative inline-flex">
      <CircleHelp className="h-3.5 w-3.5 cursor-help text-muted-foreground" />
      <span className="invisible absolute left-1/2 top-5 z-20 w-72 -translate-x-1/2 rounded-lg border bg-popover p-3 text-xs text-popover-foreground opacity-0 shadow-lg transition-opacity group-hover/help:visible group-hover/help:opacity-100">
        <span className="mb-2 block text-muted-foreground">{hint}</span>
        <span className="mb-1 block font-medium">{t('upgrade.tierPriceTitle', { unit })}</span>
        <span className="block divide-y rounded border">
          {tiers.map((tier) => {
            const label = tier.upTo == null ? t('upgrade.tierFrom', { from }) : t('upgrade.tierRange', { from, to: tier.upTo })
            from = (tier.upTo ?? 0) + 1
            return (
              <span key={label} className="flex justify-between px-2 py-1">
                <span>{label}</span>
                <span className="tabular-nums">{t('upgrade.perMonth', { amount: formatVnd(tier.unitPrice) })}</span>
              </span>
            )
          })}
        </span>
        <span className="mt-2 block text-muted-foreground">{t('upgrade.tierIncludedNote', { count: included, unit })}</span>
      </span>
    </span>
  )
}

/** Phương thức đang chọn: lựa chọn của khách nếu còn bật; chưa chọn → Chuyển khoản QR nếu bật, không thì phương thức đầu. */
export function pickMethod(methods: { id: PaymentMethod }[], chosen: PaymentMethod | null): PaymentMethod | null {
  if (chosen && methods.some((m) => m.id === chosen)) return chosen
  return methods.find((m) => m.id === DEFAULT_METHOD)?.id ?? methods[0]?.id ?? null
}

export function PaymentMethods({ methods, loading, selected, onSelect }: {
  methods: { id: PaymentMethod; label: string }[]
  loading: boolean
  selected: PaymentMethod | null
  onSelect: (id: PaymentMethod) => void
}) {
  const { t } = useTranslation('billing')
  if (loading) return <p className="text-sm text-muted-foreground">{t('upgrade.loadingMethods')}</p>
  if (methods.length === 0) {
    return (
      <p className="rounded-lg border border-warning/40 bg-warning/10 p-3 text-sm">
        {t('upgrade.onlinePaymentDisabled')}
      </p>
    )
  }
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3" role="radiogroup" aria-label={t('addon.paymentMethodTitle')}>
      {methods.map((m) => {
        const on = m.id === selected
        const copy = METHOD_COPY[m.id]
        return (
          <OptionCard key={m.id} selected={on} onClick={() => onSelect(m.id)} className="flex flex-col items-center gap-3 px-3 pb-4 pt-5">
            <Radio on={on} className="absolute left-3 top-3" />
            <MethodLogos id={m.id} size="lg" className="flex min-h-[4.5rem] flex-wrap content-center items-center justify-center gap-1.5" />
            <p className="text-center text-sm leading-snug">
              <span className="font-medium">{copy?.title ?? m.label}</span>{copy?.hint ? <span className="text-muted-foreground"> {copy.hint}</span> : null}
            </p>
          </OptionCard>
        )
      })}
    </div>
  )
}

/** Dòng "Thu qua PayPal ≈ … USD" dưới tổng tiền khi chọn PayPal. */
export function PayPalChargeNote({ method, totalVnd }: { method: PaymentMethod | null; totalVnd: number | null | undefined }) {
  const { t } = useTranslation('billing')
  const methods = useBillingMethods()
  const rate = methods.data?.methods.find((m) => m.id === 'paypal')?.vndPerUsd
  if (method !== 'paypal' || !rate || !totalVnd) return null
  return (
    <p className="-mt-2 text-right text-xs text-muted-foreground">
      {t('upgrade.paypalChargeVia')} <span className="font-medium text-foreground tabular-nums">{formatUsd(usdCentsOf(totalVnd, rate))}</span>
      {' '}{t('upgrade.paypalRate', { rate: rate.toLocaleString('vi-VN') })}
    </p>
  )
}

// ── Khối phải: xem trước đơn hàng ─────────────────────────────────────

function OrderPreview({
  currentName, currentExpires, planName, days, periodEnd, sameActive, quote, canPay, method, payload,
  invoice, invoiceLabel, onInvoiceErrors, promoCode, onPromoChange,
}: {
  currentName: string
  currentExpires: string | null
  planName: string
  days: number
  periodEnd: Date
  sameActive: boolean
  quote: ReturnType<typeof useQuote>
  canPay: boolean
  method: PaymentMethod | null
  payload: { planId: string; days: number; quantities: Quotas } | null
  invoice: InvoiceChoice | null
  invoiceLabel: string
  onInvoiceErrors: (e: InvoiceErrors) => void
  promoCode: string
  onPromoChange: (code: string) => void
}) {
  const { t } = useTranslation('billing')
  const checkout = useCheckout()
  const [redirecting, setRedirecting] = useState(false)
  const q = quote.data
  const busy = checkout.isPending || redirecting
  const stale = quote.isFetching || quote.isPlaceholderData

  const creditOnly = isFreeOrder(q)
  const modeNote = purchaseModeNote(q?.mode)
  const reqWarning = requantityWarning(q)
  // Mã đang nhập mà không áp được → chặn đặt hàng (tránh khách tưởng đã được giảm). Mã không cộng dồn mà giảm theo
  // thời hạn lợi hơn (promoNote) → không chặn, đặt hàng không kèm mã.
  const promoBlocked = isPromoBlocked(promoCode, q)

  const askRequest = useRequestPayment()
  const [asked, setAsked] = useState(false)
  const askOwner = () => {
    if (!payload) return
    askRequest.mutate({ planId: payload.planId, days: payload.days }, {
      onSuccess: (r) => { setAsked(true); toast.success(t('upgrade.toastRequestSent', { count: r.notified })) },
      onError: (err) => toast.error(apiError(err)),
    })
  }

  const pay = () => {
    if (!payload || (!method && !creditOnly) || promoBlocked) return
    // Trang khách làm dở trước khi vào đây (link kèm ?from=…) — nút "Tiếp tục" ở trang kết quả đưa về.
    const from = safeFrom(new URLSearchParams(window.location.search).get('from'))
    // Kiểm tra thông tin xuất hoá đơn TRƯỚC khi gửi — server kiểm tra lại + lưu danh bạ trước khi tạo đơn SePay.
    // Đơn 0đ (quy đổi trừ hết) không xuất hoá đơn.
    const inv = invoice && !creditOnly ? buildInvoiceBody(invoice) : { body: null, errors: {} }
    if (Object.keys(inv.errors).length) {
      onInvoiceErrors(inv.errors)
      document.getElementById('invoice-info')?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      toast.error(t('addon.checkInvoiceInfo'))
      return
    }
    checkout.mutate(
      {
        ...payload, method: method ?? undefined, invoice: creditOnly ? null : inv.body,
        ...(q?.promo ? { promoCode: q.promo.code } : {}),
        // Thanh toán xong → trang kết quả chung; `from` (nếu trang này được mở từ nơi khác) để nút "Tiếp tục" đưa về.
        returnTo: resultPath(from),
      },
      {
        onSuccess: (r) => {
          if (r.method === 'credit') {
            // Quy đổi / mã ưu đãi trừ hết giá đơn → server đã áp gói ngay.
            toast.success(q?.credit
              ? q.mode === 'requantity'
                ? t('upgrade.toastRequantityCredit')
                : t('upgrade.toastChangePlanCredit')
              : t('upgrade.toastPromoActivated'))
            window.location.assign(resultPath(from, { payment: 'return', order: r.orderId }))
            return
          }
          writePendingOrder(r.orderId, from)
          setRedirecting(true)
          try {
            goToPayment(r)
          } catch (err) {
            setRedirecting(false)
            writePendingOrder(null)
            toast.error(err instanceof Error ? err.message : t('orderActions.cannotOpenCheckout'))
          }
        },
        onError: (err) => toast.error(apiError(err)),
      },
    )
  }

  const fmtDate = (d: Date) => d.toLocaleDateString('vi-VN')

  return (
    <aside className="border-t bg-muted/40 lg:border-l lg:border-t-0">
      <div className="space-y-4 p-5 lg:sticky lg:top-0 lg:p-6">
        <h2 className="flex items-center gap-2 text-base font-medium">
          <span className="h-4 w-1 rounded-full bg-primary" /> {t('upgrade.orderPreviewTitle')}
        </h2>

        <Fold title={t('upgrade.currentPlanFold')}>
          <Row label={currentName} value={currentExpires ? t('upgrade.expiresDate', { date: fmtDate(new Date(currentExpires)), daysSuffix: daysLeft(currentExpires) > 0 ? t('upgrade.daysLeftSuffix', { days: daysLeft(currentExpires) }) : '' }) : t('upgrade.noExpiry')} />
        </Fold>

        <Fold title={q?.mode === 'requantity' ? t('upgrade.requantityFold') : sameActive ? t('upgrade.renewFold') : t('upgrade.newPlanFold')}>
          <Row label={t('upgrade.planTypeLabel')} value={planName} />
          <Row label={t('upgrade.purchaseDurationLabel')} value={t('plans.termLabel', { days })} />
          <Row
            label={q?.mode === 'extend' ? t('upgrade.newPeriodLabel') : t('upgrade.expiresAfterActivationLabel')}
            value={q?.mode === 'extend' && currentExpires
              ? `${fmtDate(new Date(currentExpires))} → ${fmtDate(periodEnd)}`
              : fmtDate(periodEnd)}
          />
          <Row label={t('upgrade.planPriceLabel')} value={q ? t('upgrade.perMonth', { amount: formatVnd(q.basePerMonth) }) : '—'} />
        </Fold>

        <Fold title={t('upgrade.extendedPlanFold')}>
          {q ? (
            <div className="space-y-2">
              {q.lines.map((l) => (
                <div key={l.key} className="flex items-start justify-between gap-3 text-sm">
                  <span className="flex items-start gap-2 text-muted-foreground">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                    <span>
                      {l.label} {formatNumber(l.quantity)}
                      {l.extra > 0 && <span className="block text-xs">{t('upgrade.includedPlusExtra', { included: l.included, extra: l.extra })}</span>}
                    </span>
                  </span>
                  <span className="shrink-0 tabular-nums">{t('upgrade.perMonth', { amount: formatVnd(l.monthly) })}</span>
                </div>
              ))}
            </div>
          ) : <p className="text-sm text-muted-foreground">—</p>}
        </Fold>

        {q && (
          <div className="space-y-1.5 border-t pt-3 text-sm">
            <Row label={t('upgrade.totalPerMonthLabel')} value={t('upgrade.perMonth', { amount: formatVnd(q.perMonth) })} />
            <Row label={t('addon.subtotalLabel', { days })} value={formatVnd(q.subtotal)} />
            {q.discount > 0 && (
              <Row
                label={<span className="flex items-center gap-1"><BadgePercent className="h-3.5 w-3.5 text-orange-500" /> {t('upgrade.durationDiscount', { percent: q.discountPercent })}</span>}
                value={<span className="text-orange-600 dark:text-orange-400">−{formatVnd(q.discount)}</span>}
              />
            )}
            {q.promo && (
              <Row
                label={
                  <span className="flex items-start gap-1">
                    <TicketPercent className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
                    <span>
                      {t('addon.promoCodeLabel', { code: q.promo.code })}
                      {!q.promo.stacked && <span className="block text-xs">{t('upgrade.insteadOfDurationDiscount')}</span>}
                    </span>
                  </span>
                }
                value={<span className="text-success">−{formatVnd(q.promo.amount)}</span>}
              />
            )}
            {q.credit && (
              <>
                <Row
                  label={<span>{t('upgrade.creditConvertedLabel', { remainingDays: q.credit.remainingDays, planWord: q.mode === 'requantity' ? t('upgrade.currentPeriodOfPlan') : t('upgrade.planWord'), planName: q.credit.fromPlanName })}</span>}
                  value={<span className="text-success">−{formatVnd(q.credit.applied)}</span>}
                />
                {q.extraDays > 0 && (
                  <Row
                    label={t('upgrade.extraDaysLabel')}
                    value={<span className="font-medium text-success">{t('upgrade.extraDaysValue', { extraDays: q.extraDays, totalDays: q.totalDays })}</span>}
                  />
                )}
              </>
            )}
            {!!q.carriedDays && (
              <Row
                label={t('upgrade.carriedDaysLabel')}
                value={<span className="font-medium text-success">{t('upgrade.carriedDaysValue', { carriedDays: q.carriedDays, totalDays: q.totalDays })}</span>}
              />
            )}
            <VatRows q={q} />
          </div>
        )}

        <PromoCodeBox
          applied={promoCode}
          promo={q?.promo ?? null}
          note={q?.promoNote ?? null}
          error={q?.promoError ?? null}
          checking={!!promoCode && stale}
          onApply={onPromoChange}
          disabled={busy}
        />

        <div className="flex items-baseline justify-between gap-3 border-t pt-4">
          <span className="text-base font-medium">{t('addon.totalPayment')}</span>
          <span className={cn('text-2xl font-semibold tabular-nums text-primary transition-opacity', stale && 'opacity-50')}>
            {q ? formatVnd(q.total) : '—'}
          </span>
        </div>
        {q?.vat && q.vat.amount > 0 && <p className="-mt-2 text-right text-xs text-muted-foreground">{t('addon.vatIncluded', { vat: vatLabel(q.vat.percent) })}</p>}
        <PayPalChargeNote method={method} totalVnd={q?.total} />
        {quote.isError && <p className="text-sm text-destructive">{apiError(quote.error)}</p>}
        <Row label={t('addon.vatInvoiceLabel')} value={<span className="block max-w-[200px] truncate" title={invoiceLabel}>{invoiceLabel}</span>} />
        {modeNote && (q?.mode !== 'change_plan' || q.credit) && (
          <div className="rounded-lg border border-primary/20 bg-primary/[0.04] p-3 text-xs">
            <p className="font-medium text-foreground">{modeNote.title}</p>
            <p className="mt-0.5 text-muted-foreground">{modeNote.text}</p>
          </div>
        )}
        {reqWarning && (
          <p role="alert" className="rounded-lg border border-warning/40 bg-warning/10 p-3 text-xs text-foreground">{reqWarning}</p>
        )}

        <Button
          size="lg"
          className="w-full"
          onClick={pay}
          disabled={!canPay || !q || stale || busy || (!method && !creditOnly) || quote.isError || promoBlocked}
        >
          {busy && <Loader2 className="animate-spin" />}
          {redirecting ? t('addon.redirecting') : creditOnly ? t('addon.activateNow') : t('addon.placeOrder')}
        </Button>
        {promoBlocked && q?.promoError && <p className="text-center text-xs text-muted-foreground">{t('upgrade.removePromoToContinue')}</p>}
        {!canPay && (
          <div className="space-y-2 rounded-lg border bg-card p-3 text-center">
            <p className="text-xs text-muted-foreground">{t('addon.onlyOwnerAdmin')}</p>
            <Button type="button" variant="outline" className="w-full" onClick={askOwner} disabled={!payload || askRequest.isPending || asked}>
              {askRequest.isPending && <Loader2 className="animate-spin" />}
              {asked ? t('upgrade.requestSent') : t('upgrade.askOwnerToPay')}
            </Button>
            {asked && <p className="text-xs text-muted-foreground">{t('upgrade.ownerNotified')}</p>}
          </div>
        )}
        <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
          <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-success" />
          {q?.vat ? `${vatListNote(q.vat.percent)} ` : ''}
          {t('addon.pricingDisclaimer')}
        </p>
      </div>
    </aside>
  )
}

/** Ô "Mã ưu đãi" + Áp dụng; đã áp → hiện mã + mô tả / lỗi + nút gỡ. */
export function PromoCodeBox({ applied, promo, note, error, checking, onApply, disabled }: {
  applied: string
  promo: Quote['promo']
  /** Ghi chú (không phải lỗi) — mã hợp lệ nhưng không được áp, vd không cộng dồn. */
  note?: string | null
  error: string | null
  checking: boolean
  onApply: (code: string) => void
  disabled?: boolean
}) {
  const { t } = useTranslation('billing')
  const [draft, setDraft] = useState('')
  const [open, setOpen] = useState(!!applied)
  const submit = () => {
    const code = normalizePromoInput(draft)
    if (!code) return
    onApply(code)
    setDraft('')
  }

  if (applied) {
    return (
      <div className={cn('rounded-lg border p-3 text-sm', error ? 'border-destructive/50 bg-destructive/5' : 'border-primary/30 bg-primary/[0.04]')}>
        <div className="flex items-center justify-between gap-2">
          <span className="flex min-w-0 items-center gap-2">
            <TicketPercent className={cn('h-4 w-4 shrink-0', error ? 'text-destructive' : 'text-primary')} />
            <span className="truncate font-mono font-medium">{applied}</span>
            {checking && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
          </span>
          <button
            type="button"
            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
            onClick={() => { onApply(''); setOpen(true) }}
            disabled={disabled}
          >
            <X className="h-3.5 w-3.5" /> {t('upgrade.removeCode')}
          </button>
        </div>
        {error ? <p className="mt-1 text-xs text-destructive">{error}</p>
          : note ? <p className="mt-1 text-xs text-muted-foreground">{note}</p>
            : promo?.description ? <p className="mt-1 text-xs text-muted-foreground">{promo.description}</p> : null}
      </div>
    )
  }

  if (!open) {
    return (
      <button type="button" className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline" onClick={() => setOpen(true)}>
        <TicketPercent className="h-4 w-4" /> {t('upgrade.havePromoCode')}
      </button>
    )
  }

  return (
    <div className="space-y-1.5">
      <label htmlFor="promo-code-input" className="text-sm font-medium">{t('upgrade.promoCodeFieldLabel')}</label>
      <div className="flex gap-2">
        <Input
          id="promo-code-input"
          value={draft}
          maxLength={32}
          placeholder={t('upgrade.enterCode')}
          className="font-mono uppercase"
          onChange={(e) => setDraft(normalizePromoInput(e.target.value))}
          onKeyDown={(e) => { if (e.key === 'Enter') submit() }}
          disabled={disabled}
        />
        <Button type="button" variant="outline" onClick={submit} disabled={disabled || !draft}>{t('upgrade.apply')}</Button>
      </div>
    </div>
  )
}

export function Fold({ title, children }: { title: string; children: ReactNode }) {
  const [open, setOpen] = useState(true)
  return (
    <div className="border-b pb-3">
      <button type="button" onClick={() => setOpen(!open)} className="flex w-full items-center justify-between py-1 text-sm font-medium">
        {title}
        <ChevronUp className={cn('h-4 w-4 text-muted-foreground transition-transform', !open && 'rotate-180')} />
      </button>
      {open && <div className="mt-2 space-y-1.5">{children}</div>}
    </div>
  )
}

/** Q2: "Tạm tính (chưa VAT)" + "VAT x%" của báo giá / đơn (đơn cũ không có quote.vat → không hiện). */
export function VatRows({ q, amount }: { q: Pick<Quote, 'vat' | 'total'> | null | undefined; amount?: number }) {
  const { t } = useTranslation('billing')
  const v = vatBreakdown(q, amount)
  if (!v) return null
  return (
    <>
      <Row label={<span className="font-medium text-foreground">{t('upgrade.subtotalPreVat')}</span>} value={<span className="font-medium">{formatVnd(v.preTax)}</span>} />
      <Row label={vatLabel(v.percent)} value={formatVnd(v.amount)} />
    </>
  )
}

export function Row({ label, value }: { label: ReactNode; value: ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-3 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right tabular-nums">{value}</span>
    </div>
  )
}
